import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { BaseEntity } from './base.entity';
import { ItemEntity } from './item.entity';
import { PurchaseOrderDetailEntity } from './purchase-order-detail.entity';
import { VendorEntity } from './vendor.entity';

@Index('vendor_item___fk_vendor__id_fk', ['fkVendorId'], {})
@Index('vendor_item__fk_item__id_fk', ['fkItemId'], {})
@Index(
	'vendor_item_externalId__deleted_userId',
	['deletedUserId', 'externalId'],
	{},
)
// PD-17414: imports look a record up by externalId alone (upserts sent with
// `deleted: true` drop the _deleted_userId filter), which the composite index
// above cannot serve, so every lookup read the whole table.
@Index('vendor_item__externalId', ['externalId'], {})
@Index(
	'vendor_item_itemExternalId_deletedDate',
	['itemExternalId', 'deletedDate'],
	{},
)
@Entity('vendor_item')
export class VendorItemEntity extends BaseEntity {
	@Column('varchar', { name: 'externalId', length: 255 })
	public externalId!: string;

	@Column('varchar', { name: 'itemExternalId', nullable: true, length: 64 })
	public itemExternalId!: string | null;

	@Column('int', { name: '_fk_vendor__id', nullable: true, unsigned: true })
	public fkVendorId!: number | null;

	// Optional — vendor items can exist without a sellable inventory item
	// (e.g. packaging materials like bags that we purchase but don't sell
	// directly). When null, the row represents a vendor-purchaseable
	// thing that isn't carried as inventory.
	@Column('int', { name: '_fk_item__id', nullable: true, unsigned: true })
	public fkItemId!: number | null;

	@Column('decimal', { name: 'cost', nullable: true, precision: 15, scale: 6 })
	public cost!: string | null;

	// PD-14587: negotiated saved price — the distributor's standard buy price,
	// human-owned. Distinct from `cost` (the vendor LIST price, owned by catalog
	// imports): repurposing `cost` would let the next import silently overwrite a
	// negotiated price. Nullable — absent until a buyer sets one.
	@Column('decimal', {
		name: 'negotiatedCost',
		nullable: true,
		precision: 15,
		scale: 6,
	})
	public negotiatedCost!: string | null;

	// PD-14587: last price actually paid, auto-maintained from PO saves (the
	// purchasing analog of client_item.lastPrice). Set by the PO save flow
	// (PD-14574), never by imports.
	@Column('decimal', {
		name: 'lastCost',
		nullable: true,
		precision: 15,
		scale: 6,
	})
	public lastCost!: string | null;

	@Column('datetime', { name: 'lastCostDate', nullable: true })
	public lastCostDate!: Date | null;

	// The PO the lastCost came from — surfaces "Last paid $x on PO-45". Stored as
	// the id (display resolves the PO number) to avoid a new relation edge into
	// the heavily-used purchase_order entity graph.
	@Column('int', {
		name: 'fk_purchase_order__id_lastCost',
		nullable: true,
		unsigned: true,
	})
	public fkPurchaseOrderIdLastCost!: number | null;

	@Column('datetime', { name: 'lastImportDate', nullable: true })
	public lastImportDate!: Date | null;

	@ManyToOne(() => ItemEntity, (item) => item.vendorItems, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_item__id', referencedColumnName: 'id' }])
	public item!: ItemEntity;

	@ManyToOne(() => VendorEntity, (vendor) => vendor.vendorItems, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_vendor__id', referencedColumnName: 'id' }])
	public vendor!: VendorEntity;

	@OneToMany(
		() => PurchaseOrderDetailEntity,
		(purchaseOrderDetail) => purchaseOrderDetail.fkVendorItem,
	)
	public purchaseOrderDetails!: Array<PurchaseOrderDetailEntity>;
}
